import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PageSkeleton } from "./components/PageSkeleton";
import { TooltipProvider } from "./components/ui/tooltip";
import { Toaster } from "./components/ui/sonner";

// Auth pages loaded eagerly (critical path)
import Login from "./pages/Login";
import Register from "./pages/Register";
import AppLayout from "./components/AppLayout";

// All tab pages lazy-loaded for performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SignalsPage = lazy(() => import("./pages/SignalsPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const PositionsPage = lazy(() => import("./pages/PositionsPage"));
const ManualOverridePage = lazy(() => import("./pages/ManualOverridePage"));
const TradesPage = lazy(() => import("./pages/TradesPage"));
const StrategiesPage = lazy(() => import("./pages/StrategiesPage"));
const BacktestPage = lazy(() => import("./pages/BacktestPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SystemPage = lazy(() => import("./pages/SystemPage"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

function LazyPage({ children }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<Suspense fallback={<PageSkeleton />}><ForgotPassword /></Suspense>} />
            <Route path="/reset-password" element={<Suspense fallback={<PageSkeleton />}><ResetPassword /></Suspense>} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<LazyPage><Dashboard /></LazyPage>} />
              <Route path="/signals" element={<LazyPage><SignalsPage /></LazyPage>} />
              <Route path="/orders" element={<LazyPage><OrdersPage /></LazyPage>} />
              <Route path="/positions" element={<LazyPage><PositionsPage /></LazyPage>} />
              <Route path="/override" element={<LazyPage><ManualOverridePage /></LazyPage>} />
              <Route path="/trades" element={<LazyPage><TradesPage /></LazyPage>} />
              <Route path="/strategies" element={<LazyPage><StrategiesPage /></LazyPage>} />
              <Route path="/backtest" element={<LazyPage><BacktestPage /></LazyPage>} />
              <Route path="/analytics" element={<LazyPage><AnalyticsPage /></LazyPage>} />
              <Route path="/system" element={<LazyPage><SystemPage /></LazyPage>} />
              <Route path="/settings" element={<LazyPage><SettingsPage /></LazyPage>} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster />
      </TooltipProvider>
    </AuthProvider>
  );
}

export default App;
